// AGC: Home - Main dashboard/home screen content
// AGC: Classic Navy Theme - White cards on off-white background
// AGC: Navy accents, gray text, clean shadows
// AGC: Lucide icons exclusively (no emoji)

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { 
  Eye,
  EyeOff,
  TrendingUp, 
  TrendingDown, 
  MapPin,
  RefreshCw,
  Loader2,
  Wheat,
  DollarSign,
  PiggyBank,
  BarChart3,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  // AGC: Quick action icons
  ListTodo,
  CloudSun,
  Package,
  Tractor,
  // AGC: Activity icons
  Droplets,
  CloudRain,
  ScanLine,
  Wallet,
  Wrench,
  Flower2,
  // AGC: Weather & Quotations icons
  Thermometer,
  Wind,
  Sun,
  Cloud,
  CloudDrizzle,
  CloudSnow,
  Coffee,
  Bean,
  Leaf,
  // AGC: Farm selector icons
  Building2,
  Check,
  // AGC: AI Advice icon
  Lightbulb,
  Sparkles,
  // AGC: Spray conditions icon
  Droplet,
} from 'lucide-react'
import { Card } from './shared'
import { RevenueDetailPanel } from './dashboard'
import { DashboardLotsSection } from './dashboard/DashboardLotsSection'
import { formatRelativeTime } from '@droidfarm/shared'
import { mockDashboardData } from '../data/financial'
import { useDashboardData } from '../hooks/useDashboardData'
import { useWeather } from '../hooks/useWeather'
import { useTranslation } from '../hooks/useTranslation'
import { useRBAC } from '../hooks/useRBAC'  // AGC: RBAC for access control
import { useIsDesktopLayout } from '../hooks/useIsDesktopLayout'  // AGC: Desktop 2-col grid
import { useFarms } from '../context/FarmContext'  // AGC: Farm selection
import { useLotes } from '../context/LotesContext'  // AGC: [DEBUG] For plots debug logging
import toast from 'react-hot-toast'
import { apiFetch } from '../services/api'
import type { Screen, QuickAction, Activity } from '../types'

// =============================================================================
// AGC: CROP ICON MAPPING
// =============================================================================

const cropIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  soja: Bean,
  soybean: Bean,
  milho: Wheat,
  corn: Wheat,
  arroz: Leaf,
  rice: Leaf,
  cafe: Coffee,
  coffee: Coffee,
  trigo: Wheat,
  wheat: Wheat,
  algodao: Flower2,
  cotton: Flower2,
  feijao: Bean,
  beans: Bean,
}

// AGC: Top 10 agricultural commodities in Brazil (most traded)
const DEFAULT_CROPS = [
  'soja',      // 1. Soja - #1 export
  'milho',     // 2. Maíz
  'cafe',      // 3. Café - Brasil es líder mundial
  'algodao',   // 4. Algodón
  'cana',      // 5. Caña de azúcar
  'trigo',     // 6. Trigo
  'arroz',     // 7. Arroz
  'feijao',    // 8. Frijol
  'laranja',   // 9. Naranja (jugo)
  'cacau',     // 10. Cacao
]

// AGC: Calcular centroide de un polígono de coordenadas
const calculateCentroid = (coordinates: [number, number][]): [number, number] => {
  if (!coordinates || coordinates.length === 0) {
    return [0, 0]
  }
  
  // AGC: Si es un solo punto, retornarlo
  if (coordinates.length === 1) {
    return coordinates[0]
  }
  
  // AGC: Calcular promedio de lat y lng
  const sum = coordinates.reduce(
    (acc, [lat, lng]) => [acc[0] + lat, acc[1] + lng],
    [0, 0]
  )
  
  return [sum[0] / coordinates.length, sum[1] / coordinates.length]
}

// AGC: Get weather icon based on condition code
const getWeatherIcon = (code: number | undefined) => {
  if (!code) return Sun
  if (code === 0) return Sun // Clear
  if (code >= 1 && code <= 3) return Cloud // Partly cloudy
  if (code >= 45 && code <= 48) return Cloud // Fog
  if (code >= 51 && code <= 67) return CloudDrizzle // Drizzle/Rain
  if (code >= 71 && code <= 77) return CloudSnow // Snow
  if (code >= 80 && code <= 99) return CloudRain // Showers/Thunderstorm
  return CloudSun
}

// AGC: Get overlay opacity based on weather condition
// AGC: Brighter conditions need darker overlay for text legibility
const getOverlayOpacity = (code: number | undefined): number => {
  if (!code) return 0.15
  // AGC: Soleado (0) necesita overlay más oscuro para legibilidad
  if (code === 0) return 0.30  // Más oscuro para soleado
  if (code >= 1 && code <= 3) return 0.20  // Parcialmente nublado
  if (code >= 45 && code <= 48) return 0.25  // Niebla
  if (code >= 51 && code <= 67) return 0.18  // Lluvia
  if (code >= 71 && code <= 77) return 0.22  // Nieve
  if (code >= 80 && code <= 99) return 0.15  // Tormenta
  return 0.15  // Default
}

// AGC: Get weather video background based on condition code
const getWeatherBackground = (code: number | undefined) => {
  if (!code || code === 0) {
    // AGC: Sunny - use weather-sunny.webp
    return {
      videoSrc: '/videos/weather-sunny.webp',
      overlay: 'bg-gradient-to-br from-amber-400/20 via-orange-300/10 to-yellow-500/20',
    }
  }
  if (code >= 1 && code <= 3) {
    // AGC: Partly cloudy - use weather-cloudy.webp
    return {
      videoSrc: '/videos/weather-cloudy.webp',
      overlay: 'bg-gradient-to-br from-slate-400/20 via-gray-300/10 to-blue-200/20',
    }
  }
  if (code >= 45 && code <= 48) {
    // AGC: Fog - use weather-cloudy.webp
    return {
      videoSrc: '/videos/weather-cloudy.webp',
      overlay: 'bg-gradient-to-br from-gray-300/30 via-slate-200/20 to-white/30',
    }
  }
  if (code >= 51 && code <= 67) {
    // AGC: Rain - use weather-stormy.webp
    return {
      videoSrc: '/videos/weather-stormy.webp',
      overlay: 'bg-gradient-to-br from-blue-500/20 via-sky-400/10 to-blue-300/20',
    }
  }
  if (code >= 71 && code <= 77) {
    // AGC: Snow - use weather-cloudy.webp
    return {
      videoSrc: '/videos/weather-cloudy.webp',
      overlay: 'bg-gradient-to-br from-cyan-200/30 via-sky-100/20 to-blue-200/30',
    }
  }
  if (code >= 80 && code <= 99) {
    // AGC: Thunderstorm - use weather-stormy.webp
    return {
      videoSrc: '/videos/weather-stormy.webp',
      overlay: 'bg-gradient-to-br from-slate-600/30 via-gray-500/20 to-slate-400/20',
    }
  }
  // AGC: Default - use weather-sunny.webp
  return {
    videoSrc: '/videos/weather-sunny.webp',
    overlay: 'bg-gradient-to-br from-slate-300/20 via-gray-200/10 to-slate-100/20',
  }
}

interface HomeProps {
  onNavigate: (screen: Screen) => void
}

// AGC: Map icon names to Lucide components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  MapPin,
  ListTodo,
  CloudSun,
  Package,
  Droplets,
  CloudRain,
  ScanLine,
  Wallet,
  Wrench,
  Flower2,
  DollarSign,
}

/**
 * AGC: Home - Dashboard with Classic Navy Theme
 * AGC: Features:
 *   - White hero card with soft shadow
 *   - Navy accents, gray text
 *   - Monochrome Lucide icons for quick actions
 *   - Clean activity feed
 */
export const Home: React.FC<HomeProps> = ({ onNavigate }) => {
  // AGC: [#4] Currency-aware formatCurrency + currencyInfo from the hook (respects the user's selected
  // AGC: currency), NOT the shared pt-BR/BRL-hardcoded formatter.
  const { t, formatCurrency, currencyInfo } = useTranslation()
  const { role, permissions, isOwner, isManager, isWorker } = useRBAC()  // AGC: Get user role and permissions
  // AGC: Desktop layout switch — when true the dashboard renders a 2-col grid (never true on Capacitor)
  const isDesktop = useIsDesktopLayout()
  
  const { plots, isLoading: isLoadingPlots } = useLotes()  // AGC: Get lots for dashboard calculations
  
  // AGC: Deduplicate plots by id for display to avoid React "duplicate key" warnings
  // AGC: Can happen when same plot appears under multiple fields or from sync race
  const plotsForDisplay = useMemo(() => {
    const active = plots.filter(p => !p.isArchived)
    const byId = new Map(active.map(p => [p.id, p]))
    return Array.from(byId.values())
  }, [plots])
  
  // AGC: Lot selector state for Weather card - MUST be declared before weatherCoordinates useMemo
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null)
  const [showLotSelector, setShowLotSelector] = useState(false)
  const lotSelectorRef = useRef<HTMLDivElement>(null)
  
  const { 
    financials, 
    prices,
    priceSource,
    priceUpdatedAt,
    activities, 
    isLoading, 
    isLoadingPrices,
    isFetchingNewPrices,
    refresh,
    fetchNewPricesToFirebase,
  } = useDashboardData()
  
  // AGC: Farm selection context
  const { 
    farms, 
    currentFarm, 
    currentFarmDisplayName, 
    setCurrentFarm,
    needsWizard,
    isLoading: isLoadingFarms 
  } = useFarms()
  
  // AGC: Calculate weather coordinates based on selected lot or farm
  const weatherCoordinates = useMemo(() => {
    if (selectedLotId) {
      const selectedLot = plots.find(p => p.id === selectedLotId)
      if (selectedLot?.coordinates && selectedLot.coordinates.length > 0) {
        // AGC: Calculate centroid of the lot polygon
        const centroid = calculateCentroid(selectedLot.coordinates)
        return { lat: centroid[0], lon: centroid[1] }
      }
    }
    // AGC: Fallback to farm coordinates
    if (currentFarm?.coordinates && currentFarm.coordinates.length === 2) {
      return { lat: currentFarm.coordinates[0], lon: currentFarm.coordinates[1] }
    }
    return undefined
  }, [selectedLotId, plots, currentFarm])
  
  // AGC: Weather data for dashboard widget - uses lot or farm coordinates
  const { current, isLoading: isLoadingWeather, location: weatherLocation, sprayConditions } = useWeather({ coordinates: weatherCoordinates })
  
  const currentWeather = current ?? null
  
  const { quickActions } = mockDashboardData
  
  // AGC: Filter quick actions based on permissions — hide Finanças for users without canViewFinancial
  const filteredQuickActions = useMemo(() => {
    if (!permissions?.canViewFinancial) {
      return quickActions.filter(action => action.id !== 'expenses' && action.id !== 'finances')
    }
    return quickActions
  }, [quickActions, permissions])
  
  // AGC: Use top 10 Brazilian commodities (always show all 10)
  const quotationCrops = useMemo(() => {
    return DEFAULT_CROPS
  }, [])
  
  // AGC: Generate price data with simulated daily variation
  const quotationData = useMemo(() => {
    return quotationCrops.map(cropType => {
      const priceData = prices[cropType]
      const currentPrice = priceData?.preco || priceData?.valor_saca || 0
      
      // AGC: Simulate daily variation (±5%) for demo
      // AGC: In production, this would come from historical price data
      const seed = cropType.charCodeAt(0) + new Date().getDate()
      const variation = ((seed % 10) - 5) / 100 // -5% to +5%
      const changePercent = variation * 100
      
      return {
        crop: cropType,
        price: currentPrice,
        changePercent,
        isPositive: changePercent >= 0,
      }
    })
  }, [quotationCrops, prices])
  
  // AGC: State for hiding/showing balance
  const [showBalance, setShowBalance] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  // AGC: Revenue detail expanded by default on desktop so the full breakdown is visible immediately
  const [showRevenueDetail, setShowRevenueDetail] = useState(() => isDesktop)
  
  // AGC: Farm selector dropdown state
  const [showFarmSelector, setShowFarmSelector] = useState(false)
  const farmSelectorRef = useRef<HTMLDivElement>(null)
  
  // AGC: Carousel state for Weather/Quotations/AI Advice
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isCarouselPaused, setIsCarouselPaused] = useState(false)
  const carouselRef = useRef<HTMLDivElement>(null)
  const isScrollingRef = useRef(false) // AGC: Track if we're programmatically scrolling
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const totalSlides = 3
  
  // AGC: Single expansion state for all carousel cards - expand/collapse together
  // AGC: Default to expanded on desktop so weather detail is visible without a click
  const [cardsExpanded, setCardsExpanded] = useState(() => isDesktop)
  const expandedWeather = cardsExpanded
  const expandedQuotations = cardsExpanded
  const expandedAIAdvice = cardsExpanded
  const anyCardExpanded = cardsExpanded
  
  // AGC: Auto-scroll carousel every 5 seconds
  useEffect(() => {
    if (isCarouselPaused) return
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides)
    }, 5000)
    
    return () => clearInterval(interval)
  }, [isCarouselPaused])
  
  // AGC: Scroll to current slide when it changes (only from auto-scroll or dots)
  useEffect(() => {
    if (carouselRef.current && !isScrollingRef.current) {
      isScrollingRef.current = true // AGC: Mark as programmatic scroll
      const slideWidth = carouselRef.current.offsetWidth
      carouselRef.current.scrollTo({
        left: currentSlide * slideWidth,
        behavior: 'smooth'
      })
      // AGC: Reset flag after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false
      }, 500)
    }
  }, [currentSlide])
  
  // AGC: Handle manual scroll and update current slide
  const handleCarouselScroll = () => {
    // AGC: Ignore scroll events triggered by programmatic scrolling
    if (isScrollingRef.current || !carouselRef.current) return
    
    // AGC: Clear any pending timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    
    // AGC: Pause auto-scroll while user is manually scrolling
    setIsCarouselPaused(true)
    
    // AGC: Debounce: update slide after user stops scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth
        const scrollLeft = carouselRef.current.scrollLeft
        const newSlide = Math.round(scrollLeft / slideWidth)
        if (newSlide !== currentSlide) {
          setCurrentSlide(newSlide)
        }
        // AGC: Resume auto-scroll after 3 seconds of inactivity
        setTimeout(() => {
          setIsCarouselPaused(false)
        }, 3000)
      }
    }, 150) // AGC: Wait 150ms after scroll stops
  }
  
  // AGC: AI suggestion from Suggestion Agent (replaces hardcoded tips)
  const [aiSuggestion, setAiSuggestion] = useState<{
    mainTip: string
    tips: string[]
    category: string
    generatedAt: string
  } | null>(null)
  const [aiLoading, setAiLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function fetchSuggestion() {
      try {
        const data = await apiFetch<{ success: boolean; suggestion: any }>('/api/v1/suggestions')
        if (!cancelled && data.success && data.suggestion) {
          setAiSuggestion(data.suggestion)
        }
      } catch {
        // AGC: Silently fail — card shows fallback
      }
    }

    fetchSuggestion()
    return () => { cancelled = true }
  }, [])

  const handleRefreshSuggestion = async () => {
    if (aiLoading) return
    setAiLoading(true)
    try {
      const data = await apiFetch<{ success: boolean; suggestion: any }>('/api/v1/suggestions/refresh', {
        method: 'POST',
      })
      if (data.success && data.suggestion) {
        setAiSuggestion(data.suggestion)
      }
    } catch {
      // AGC: Silently fail
    } finally {
      setAiLoading(false)
    }
  }
  
  // AGC: Close farm selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (farmSelectorRef.current && !farmSelectorRef.current.contains(event.target as Node)) {
        setShowFarmSelector(false)
      }
    }
    
    if (showFarmSelector) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFarmSelector])
  
  // AGC: Close lot selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (lotSelectorRef.current && !lotSelectorRef.current.contains(event.target as Node)) {
        setShowLotSelector(false)
      }
    }
    
    if (showLotSelector) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLotSelector])
  
  // AGC: Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  // AGC: Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refresh()
    setIsRefreshing(false)
  }

  // AGC: Format large currency values with K/M suffixes
  const formatLargeValue = (value: number): string => {
    if (!showBalance) return '••••••'
    if (value >= 1000000) {
      return `${currencyInfo.symbol} ${(value / 1000000).toFixed(2)}M`
    }
    if (value >= 10000) {
      return `${currencyInfo.symbol} ${(value / 1000).toFixed(0)}K`
    }
    return formatCurrency(value)
  }

  // AGC: Calculate margin percentage
  const marginPercentage = useMemo(() => {
    if (financials.projectedIncome === 0) return 0
    return (financials.grossMargin / financials.projectedIncome) * 100
  }, [financials.grossMargin, financials.projectedIncome])

  const isPositiveMargin = financials.grossMargin >= 0

  return (
    // AGC: Desktop: 2-column grid (left=financials/actions/lots, right=carousel/activities)
    // AGC: Mobile: unchanged vertical flow — isDesktop is never true on Capacitor native
    <div
      className={`min-h-full bg-[#F4F5F7] dark:bg-slate-900 transition-colors`}
      style={isDesktop ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' } : undefined}
    >
      {/* AGC: Hero Card - NOW A FEATURED CARD (matches dashboard card system) */}
      {/* AGC: No longer full-bleed, now properly integrated with gutters + rounded corners */}
      {/* AGC: RBAC - Only show financial data for owner/manager */}
      {permissions?.canViewProjectedValue && (
      // AGC: Desktop col 1 row 1 — projected revenue hero card
      <div className={isDesktop ? 'animate-fade-in' : 'px-4 pt-3 animate-fade-in'} style={isDesktop ? { gridColumn: '1' } : undefined}>
        <Card variant="featured" padding="sm" className="relative overflow-hidden bg-[#46B179]">
          {/* AGC: Glassmorphism overlay - enhanced transparency */}
          <div className="absolute inset-0 bg-white/20 backdrop-blur-lg pointer-events-none" />
          
          {/* AGC: Decorative background effects - subtle light glows */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* AGC: Top-right light glow */}
            <div 
              className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-15"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
            />
          </div>
          
          <div className="relative z-10">
            {/* AGC: Compact Revenue Card - Minimized vertical height */}
            {/* AGC: Row 1: Farm Name Selector + Lots/Area + Action buttons */}
            <div className="flex items-center justify-between mb-0.5">
              {/* AGC: Left side - Farm selector and lots/area info */}
              <div className="flex items-center gap-3">
                {/* AGC: Farm Name Selector - Clickable dropdown */}
                <div className="relative" ref={farmSelectorRef}>
                  <button
                    onClick={() => setShowFarmSelector(!showFarmSelector)}
                    className="flex items-center gap-1.5 text-white/80 text-xs font-medium hover:text-white transition-colors"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[120px]">
                      {currentFarmDisplayName || t('farm.selectFarm')}
                    </span>
                    {farms.length > 1 && (
                      <ChevronDown className={`w-3 h-3 transition-transform ${showFarmSelector ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  
                  {/* AGC: Farm Selector Dropdown */}
                  {showFarmSelector && farms.length > 1 && (
                    <div className="absolute top-full left-0 mt-1 min-w-[180px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 py-1 animate-fade-in">
                      {/* AGC: All Farms option */}
                      <button
                        onClick={() => {
                          setCurrentFarm(null)
                          setShowFarmSelector(false)
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          !currentFarm ? 'text-droid-600 dark:text-droid-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <Building2 className="w-4 h-4" />
                        <span>{t('farm.allFarms')}</span>
                        {!currentFarm && <Check className="w-4 h-4 ml-auto" />}
                      </button>
                      
                      {/* AGC: Divider */}
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                      
                      {/* AGC: Individual farms */}
                      {farms.map((farm) => (
                        <button
                          key={farm.id}
                          onClick={() => {
                            setCurrentFarm(farm)
                            setShowFarmSelector(false)
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                            currentFarm?.id === farm.id ? 'text-droid-600 dark:text-droid-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <Building2 className="w-4 h-4" />
                          <span className="truncate">{farm.name}</span>
                          {currentFarm?.id === farm.id && <Check className="w-4 h-4 ml-auto flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* AGC: Separator */}
                <span className="text-white/30">•</span>
                
                {/* AGC: Lots count and total area */}
                <div className="flex items-center gap-2 text-white/50 text-xs">
                  <span>{financials.lotCount} {t('entity.lot_plural').toLowerCase()}</span>
                  <span>•</span>
                  <span>{financials.totalAreaHa.toFixed(1)} ha</span>
                </div>
              </div>
              
              {/* AGC: Action buttons (right) */}
              <div className="flex items-center gap-0.5">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label={t('common.refresh')}
                >
                  {isRefreshing ? (
                    <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 text-white/70" />
                  )}
                </button>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label={showBalance ? t('dashboard.hideBalance') : t('dashboard.showBalance')}
                >
                  {showBalance ? (
                    <Eye className="w-3.5 h-3.5 text-white/70" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-white/70" />
                  )}
                </button>
              </div>
            </div>

            {/* AGC: Clickable area for expand/collapse - covers everything below the top row */}
            <button
              onClick={() => setShowRevenueDetail(!showRevenueDetail)}
              className="w-full text-left focus:outline-none"
              aria-label={showRevenueDetail ? t('dashboard.hideDetails') : t('dashboard.viewDetails')}
            >
              {/* AGC: Row 2: Title + Main value + Chevrons */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {/* AGC: Title - Receita Projetada / Ingresos Proyectados / Projected Income */}
                  <div className="text-white/70 text-[10px] font-medium capitalize tracking-wide mb-0.5">
                    {t('dashboard.projectedIncome')}
                  </div>
                  
                  {/* AGC: Main Balance Display + Variation indicator */}
                  <div className="flex items-center gap-3">
                    {isLoading ? (
                      <div className="h-7 bg-white/20 rounded animate-pulse w-32" />
                    ) : (
                      <h2 className="text-2xl font-bold text-white tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {showBalance ? formatCurrency(financials.projectedIncome) : `${currencyInfo.symbol} ••••••`}
                      </h2>
                    )}
                    
                    {/* AGC: Revenue Variation Indicator */}
                    {showBalance && !isLoading && financials.projectedIncome > 0 && (
                      <div className={`flex items-center gap-0.5 text-sm font-medium ${
                        marginPercentage >= 0 ? 'text-green-300' : 'text-red-300'
                      }`}>
                        {marginPercentage >= 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        <span>{marginPercentage >= 0 ? '+' : ''}{marginPercentage.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* AGC: Chevrons on the right - animated when collapsed */}
                {!showRevenueDetail && (
                  <div className="flex items-center text-white/60 hover:text-white/80 transition-colors ml-2">
                    <ChevronsDown className="w-4 h-4 animate-bounce" style={{ animationDuration: '1.5s', animationIterationCount: 'infinite' }} />
                  </div>
                )}
                {showRevenueDetail && (
                  <div className="flex items-center text-white/60 hover:text-white/80 transition-colors ml-2">
                    <ChevronsUp className="w-4 h-4" />
                  </div>
                )}
              </div>
            </button>

            {/* AGC: Expandable Details Section - Cost & Margin inside hero card */}
            {showRevenueDetail && (
              <div className="mt-2 pt-2 border-t border-white/10 animate-fade-in">
                {/* AGC: Cost and Margin KPIs */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {/* AGC: Projected Cost - with variation indicator (same pattern as revenue) */}
                  <button 
                    onClick={() => onNavigate('expenses')}
                    className="bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl p-2.5 text-left transition-colors border border-white/10"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <DollarSign className="w-3.5 h-3.5 text-red-300" />
                      <span className="text-white/60 text-xs font-medium capitalize tracking-wide">
                        {t('dashboard.projectedCost')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-white text-base font-bold">
                        {isLoading ? '...' : formatLargeValue(financials.projectedCost)}
                      </p>
                      {/* AGC: Cost variation indicator - for costs: up = bad (red), down = good (green) */}
                      {showBalance && financials.executedExpenses > 0 && (
                        <div className={`flex items-center gap-0.5 text-xs font-medium ${
                          financials.executedExpenses < financials.projectedCost ? 'text-green-300' : 'text-red-300'
                        }`}>
                          {financials.executedExpenses < financials.projectedCost ? (
                            <TrendingDown className="w-3 h-3" />
                          ) : (
                            <TrendingUp className="w-3 h-3" />
                          )}
                          <span>
                            {((financials.executedExpenses / financials.projectedCost) * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* AGC: Gross Margin */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2.5 border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <PiggyBank className="w-3.5 h-3.5 text-green-300" />
                      <span className="text-white/60 text-xs font-medium capitalize tracking-wide">
                        {t('dashboard.grossMargin')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-white text-base font-bold">
                        {isLoading ? '...' : formatLargeValue(financials.grossMargin)}
                      </p>
                      {/* AGC: Margin variation indicator */}
                      {showBalance && (
                        <div className={`flex items-center gap-0.5 text-xs font-medium ${isPositiveMargin ? 'text-green-300' : 'text-red-300'}`}>
                          {isPositiveMargin ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          <span>{marginPercentage.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* AGC: Revenue Detail Panel (lot breakdown) */}
                <RevenueDetailPanel
                  lotProjections={financials.lotProjections}
                  prices={prices}
                  isExpanded={true}
                  onClose={() => setShowRevenueDetail(false)}
                  showBalance={showBalance}
                  onNavigate={onNavigate}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
      )}

      {/* AGC: Desktop right column wrapper — Clima + Atalhos + Atividades stack independently */}
      {/* AGC: Desktop right col row 1 — Clima carousel + Atalhos stacked */}
      {/* AGC: On mobile: transparent wrapper, children keep own px/mt classes */}
      <div
        style={isDesktop ? { gridColumn: '2', gridRow: '1' } : undefined}
        className={isDesktop ? 'flex flex-col gap-3' : ''}
      >

      {/* AGC: Carousel Section - Weather, Quotations, AI Advice */}
      {/* AGC: Reduced spacing for more condensed layout */}
      <div className={isDesktop ? '' : 'px-4 mt-2'}>
        {/* AGC: Carousel Container */}
        <div
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          onTouchStart={() => {
            setIsCarouselPaused(true)
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current)
            }
          }}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* AGC: Card 1 - Weather with video background + Spray Conditions */}
          <div
            className="flex-shrink-0 w-full snap-start"
          >
            {(() => {
              const weatherBg = getWeatherBackground(currentWeather?.weatherCode)
              const WeatherIcon = getWeatherIcon(currentWeather?.weatherCode)
              const deltaT = sprayConditions?.deltaT?.value ?? currentWeather?.deltaT ?? 5
              const overlayOpacity = getOverlayOpacity(currentWeather?.weatherCode)
              
              // AGC: Calculate spray condition status
              const getSprayStatus = (value: number) => {
                if (value >= 2 && value <= 8) return {
                  label: t('weather.sprayFavorable'),
                  color: 'text-emerald-600 dark:text-emerald-400',
                  bgColor: 'bg-emerald-500/20',
                  progress: 100
                }
                if ((value > 8 && value <= 10) || (value >= 1 && value < 2)) return {
                  label: t('weather.sprayModerate'),
                  color: 'text-amber-600 dark:text-amber-400',
                  bgColor: 'bg-amber-500/20',
                  progress: 60
                }
                return {
                  label: t('weather.sprayUnfavorable'),
                  color: 'text-red-600 dark:text-red-400',
                  bgColor: 'bg-red-500/20',
                  progress: 30
                }
              }
              const sprayStatus = getSprayStatus(deltaT)
              
              return (
                <div 
                  className={`relative overflow-hidden rounded-2xl border border-white/30 dark:border-white/10 shadow-lg transition-all duration-200 ${
                    expandedWeather ? 'h-auto' : anyCardExpanded ? 'h-auto min-h-[110px]' : 'h-[110px]'
                  }`}
                >
                  {/* AGC: Weather Background - Animated WebP (direct, no blur) */}
                  <img
                    src={weatherBg.videoSrc}
                    alt="Weather background"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  
                  {/* AGC: Dynamic dark overlay for text readability - opacity based on weather condition */}
                  <div 
                    className="absolute inset-0 bg-black" 
                    style={{ opacity: overlayOpacity }}
                  />
                  
                  {/* AGC: Chevrons animados - expand/collapse indicator (centro inferior) */}
                  {!expandedWeather && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setCardsExpanded(true)
                      }}
                      className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-white/60 hover:text-white/80 transition-colors z-20 focus:outline-none"
                    >
                      <ChevronsDown className="w-4 h-4 animate-bounce" style={{ animationDuration: '1.5s', animationIterationCount: 'infinite' }} />
                    </button>
                  )}
                  {expandedWeather && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setCardsExpanded(false)
                      }}
                      className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-white/60 hover:text-white/80 transition-colors z-20 focus:outline-none"
                    >
                      <ChevronsUp className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* AGC: Content - pb-10 para dar espacio a chevrons en centro inferior */}
                  <div className="relative z-10 p-3 pb-10 h-full flex flex-col justify-between">
                    {isLoadingWeather ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
                      </div>
                    ) : currentWeather ? (
                      <>
                        {/* AGC: Top row - Location and Temperature */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* AGC: Lot/Farm Selector */}
                            <div className="relative" ref={lotSelectorRef}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setShowLotSelector(!showLotSelector)
                                }}
                                className="flex items-center gap-1 text-white text-[10px] mb-0.5 drop-shadow-md hover:text-white/80 transition-colors"
                              >
                                <MapPin className="w-2.5 h-2.5" />
                                <span className="truncate max-w-[100px]">
                                  {selectedLotId 
                                    ? plots.find(p => p.id === selectedLotId)?.name || currentFarmDisplayName
                                    : currentFarmDisplayName || weatherLocation || t('dashboard.weather')
                                  }
                                </span>
                                <ChevronDown className="w-2.5 h-2.5" />
                              </button>
                              
                              {/* AGC: Dropdown menu */}
                              {showLotSelector && (
                                <div className="absolute top-full left-0 mt-1 min-w-[180px] max-w-[240px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 py-1 max-h-[200px] overflow-y-auto">
                                  {/* AGC: Farm option (all lots) */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedLotId(null)
                                      setShowLotSelector(false)
                                    }}
                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                      !selectedLotId ? 'bg-droid-50 dark:bg-droid-900/20 text-droid-700 dark:text-droid-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-3.5 h-3.5" />
                                      <span className="truncate">{currentFarmDisplayName || t('farm.allFarms')}</span>
                                      {!selectedLotId && <Check className="w-3.5 h-3.5 ml-auto flex-shrink-0" />}
                                    </div>
                                  </button>
                                  
                                  {plotsForDisplay.length > 0 && (
                                    <>
                                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                                      {plotsForDisplay.map(lot => (
                                        <button
                                          key={lot.id}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setSelectedLotId(lot.id)
                                            setShowLotSelector(false)
                                          }}
                                          className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                            selectedLotId === lot.id ? 'bg-droid-50 dark:bg-droid-900/20 text-droid-700 dark:text-droid-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <MapPin className="w-3.5 h-3.5" />
                                            <div className="flex-1 min-w-0">
                                              <p className="truncate">{lot.name}</p>
                                              <p className="text-[10px] text-gray-500 dark:text-gray-400">{lot.area.toFixed(1)} ha</p>
                                            </div>
                                            {selectedLotId === lot.id && <Check className="w-3.5 h-3.5 ml-auto flex-shrink-0" />}
                                          </div>
                                        </button>
                                      ))}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl font-light text-white drop-shadow-lg">
                                {Math.round(currentWeather.temperature)}°
                              </span>
                              <div className="flex items-center gap-1 text-[10px] text-white drop-shadow-md">
                                <Wind className="w-2.5 h-2.5" />
                                <span>{Math.round(currentWeather.windSpeed)} km/h</span>
                              </div>
                            </div>
                          </div>
                          <WeatherIcon className="w-9 h-9 text-white drop-shadow-lg flex-shrink-0" />
                        </div>
                        
                        {/* AGC: Bottom row - Spray Conditions */}
                        <div className="flex items-center justify-between gap-2">
                          {/* AGC: Spray info */}
                          <div className="flex items-center gap-1.5">
                            <div className={`p-1 rounded-md ${sprayStatus.bgColor} backdrop-blur-sm`}>
                              <Droplet className="w-3 h-3 text-white drop-shadow-md" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[9px] text-white drop-shadow-md leading-none">{t('weather.spray')}</span>
                              <span className={`text-[11px] font-semibold ${sprayStatus.color} drop-shadow-md leading-tight`}>
                                {sprayStatus.label}
                              </span>
                            </div>
                          </div>
                          
                          {/* AGC: Delta-T value with mini progress */}
                          <div className="flex flex-col items-end gap-0.5">
                            <span className={`text-xs font-bold ${sprayStatus.color} drop-shadow-md`}>
                              ΔT {deltaT.toFixed(1)}°
                            </span>
                            {/* AGC: Mini progress bar */}
                            <div className="w-16 h-1 rounded-full bg-gray-300/50 dark:bg-gray-600/50 overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-1000 ${
                                  sprayStatus.progress === 100 ? 'bg-emerald-500' :
                                  sprayStatus.progress === 60 ? 'bg-amber-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${sprayStatus.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <CloudSun className="w-8 h-8 mr-2 opacity-50" />
                        <span className="text-xs">{t('dashboard.noWeather')}</span>
                      </div>
                    )}
                    
                    {/* AGC: Expanded Weather - Show lot-by-lot weather details */}
                    {expandedWeather && currentWeather && plotsForDisplay.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/20 animate-fade-in">
                        <h4 className="text-xs font-semibold text-white drop-shadow-md mb-2">
                          {t('dashboard.weatherByLot')}
                        </h4>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                          {plotsForDisplay.slice(0, 5).map(plot => {
                            // AGC: Calculate spray status based on Delta-T (same logic as main card)
                            const getSprayStatusForLot = (deltaT: number) => {
                              if (deltaT >= 2 && deltaT <= 8) return { 
                                label: 'Ideal', 
                                color: 'text-emerald-400',
                                bgColor: 'bg-emerald-500/20',
                                icon: '✓'
                              }
                              if ((deltaT > 8 && deltaT <= 10) || (deltaT >= 1 && deltaT < 2)) return { 
                                label: 'Marginal', 
                                color: 'text-amber-400',
                                bgColor: 'bg-amber-500/20',
                                icon: '⚠'
                              }
                              return { 
                                label: 'Inadequado', 
                                color: 'text-red-400',
                                bgColor: 'bg-red-500/20',
                                icon: '✗'
                              }
                            }
                            
                            const lotSprayStatus = getSprayStatusForLot(deltaT)
                            
                            return (
                              <div 
                                key={plot.id}
                                className="flex items-center justify-between p-2 rounded-lg bg-white/10 backdrop-blur-sm"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-white truncate drop-shadow-md">
                                    {plot.name}
                                  </p>
                                  <p className="text-[10px] text-white/70 drop-shadow-md">
                                    {plot.area.toFixed(1)} ha
                                  </p>
                                </div>
                                <div className="flex items-center gap-3">
                                  {/* AGC: Temperature */}
                                  <div className="flex items-center gap-1">
                                    <Thermometer className="w-3 h-3 text-white/70" />
                                    <span className="text-xs font-medium text-white drop-shadow-md">
                                      {Math.round(currentWeather.temperature)}°
                                    </span>
                                  </div>
                                  {/* AGC: Delta-T */}
                                  <div className="flex items-center gap-1">
                                    <Droplet className="w-3 h-3 text-white/70" />
                                    <span className="text-xs font-medium text-white drop-shadow-md">
                                      ΔT {deltaT.toFixed(1)}°
                                    </span>
                                  </div>
                                  {/* AGC: Spray Status Badge - NEW */}
                                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md ${lotSprayStatus.bgColor}`}>
                                    <span className="text-[10px]">{lotSprayStatus.icon}</span>
                                    <span className={`text-[10px] font-semibold ${lotSprayStatus.color} drop-shadow-md`}>
                                      {lotSprayStatus.label}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        {plotsForDisplay.length > 5 && (
                          <p className="text-[10px] text-white/60 mt-2 text-center drop-shadow-md">
                            {t('dashboard.showingFirstLots', { count: 5 })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>

          {/* AGC: Card 2 - Crop Quotations - with glassmorphism */}
          <div className="flex-shrink-0 w-full snap-start">
            <div className={`relative overflow-hidden rounded-2xl border border-white/30 dark:border-white/10 shadow-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl transition-all duration-200 ${
              expandedQuotations ? 'h-auto min-h-[280px]' : anyCardExpanded ? 'h-auto min-h-[120px]' : 'h-[120px]'
            }`}>
              {/* AGC: Chevrons animados - expand/collapse indicator (centro inferior) */}
              {!expandedQuotations && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setCardsExpanded(true)
                  }}
                  className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-droid-600/60 hover:text-droid-600 dark:text-droid-400/60 dark:hover:text-droid-400 transition-colors z-20 focus:outline-none"
                >
                  <ChevronsDown className="w-4 h-4 animate-bounce" style={{ animationDuration: '1.5s', animationIterationCount: 'infinite' }} />
                </button>
              )}
              {expandedQuotations && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setCardsExpanded(false)
                  }}
                  className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-droid-600/60 hover:text-droid-600 dark:text-droid-400/60 dark:hover:text-droid-400 transition-colors z-20 focus:outline-none"
                >
                  <ChevronsUp className="w-4 h-4" />
                </button>
              )}
              
              <div className="p-4 pb-10 h-full flex flex-col">
                {/* AGC: Header: left = title + Fetch new prices, right = location + date/time */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <BarChart3 className="w-4 h-4 text-droid-600 dark:text-droid-400 flex-shrink-0" />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 capitalize tracking-wide">
                      {t('dashboard.quotations')}
                    </span>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation()
                        const result = await fetchNewPricesToFirebase()
                        if (result.success) {
                          toast.success(t('dashboard.pricesUpdated'))
                        } else if (result.error) {
                          toast.error(t('dashboard.pricesUpdateFailed') + ': ' + result.error)
                        }
                      }}
                      disabled={isFetchingNewPrices}
                      className="flex items-center gap-1 text-[10px] font-medium text-droid-600 hover:text-droid-700 dark:text-droid-400 dark:hover:text-droid-300 disabled:opacity-50"
                      title={t('dashboard.fetchNewPrices')}
                    >
                      {isFetchingNewPrices ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      <span className="hidden sm:inline">{t('dashboard.fetchNewPrices')}</span>
                    </button>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {priceSource && (
                      <>
                        <div className="text-[9px] text-gray-400">{priceSource.cidade}</div>
                        {priceUpdatedAt && (
                          <div className="text-[8px] text-gray-400/80">
                            {t('dashboard.lastUpdate')}: {formatRelativeTime(priceUpdatedAt)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* AGC: Crop prices - expandable grid (2x2 or 2x5) */}
                {isLoadingPrices ? (
                  <div className="flex items-center justify-center flex-1">
                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 flex-1">
                    {quotationData.slice(0, expandedQuotations ? 10 : 4).map((item) => {
                      const CropIcon = cropIconMap[item.crop] || Wheat
                      const shortName = item.crop.split(' ')[0].replace('_', ' ')
                      // AGC: Normalize crop name for price lookup - improved matching
                      const normalizedCrop = item.crop.toLowerCase().replace(/\s+/g, '_')
                      const priceValue = prices[normalizedCrop]?.preco || prices[item.crop.toLowerCase()]?.preco || prices[item.crop]?.preco || item.price
                      
                      return (
                        <div key={item.crop} className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <CropIcon className="w-4 h-4 text-droid-600 dark:text-droid-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600 dark:text-gray-400 capitalize truncate font-medium">{shortName}</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {priceValue > 0 ? (
                              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                R${priceValue.toFixed(0)}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 font-medium">--</span>
                            )}
                            <div className={`flex items-center text-[10px] font-medium ${
                              item.isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                            }`}>
                              {item.isPositive ? (
                                <TrendingUp className="w-3 h-3" />
                              ) : (
                                <TrendingDown className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AGC: Card 3 - AI Advice - with glassmorphism */}
          <div className="flex-shrink-0 w-full snap-start">
            <div className={`relative overflow-hidden rounded-2xl border border-white/30 dark:border-amber-800/50 shadow-lg bg-gradient-to-br from-amber-50/70 to-orange-50/70 dark:from-amber-900/30 dark:to-orange-900/30 backdrop-blur-xl transition-all duration-200 ${
              expandedAIAdvice ? 'h-auto min-h-[110px]' : anyCardExpanded ? 'h-auto min-h-[110px]' : 'h-[110px]'
            }`}>
              <div className="absolute top-2 right-2 opacity-10">
                <Sparkles className="w-16 h-16 text-amber-600" />
              </div>
              
              {/* AGC: Chevrons animados - expand/collapse indicator (centro inferior) */}
              {!expandedAIAdvice && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setCardsExpanded(true)
                  }}
                  className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-amber-600/60 hover:text-amber-600 dark:text-amber-400/60 dark:hover:text-amber-400 transition-colors z-20 focus:outline-none"
                >
                  <ChevronsDown className="w-4 h-4 animate-bounce" style={{ animationDuration: '1.5s', animationIterationCount: 'infinite' }} />
                </button>
              )}
              {expandedAIAdvice && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setCardsExpanded(false)
                  }}
                  className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center text-amber-600/60 hover:text-amber-600 dark:text-amber-400/60 dark:hover:text-amber-400 transition-colors z-20 focus:outline-none"
                >
                  <ChevronsUp className="w-4 h-4" />
                </button>
              )}
              
              <div className="p-3 pb-10 h-full flex flex-col relative z-10">
                {/* AGC: Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <div className="p-1 rounded-lg bg-amber-100 dark:bg-amber-800/30">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-semibold text-amber-800 dark:text-amber-300 capitalize tracking-wide">
                        {t('dashboard.aiAdvice.title')}
                      </span>
                      {aiSuggestion?.generatedAt && (
                        <span className="text-[8px] text-amber-600/60 dark:text-amber-400/50">
                          {(() => {
                            const days = Math.floor((Date.now() - new Date(aiSuggestion.generatedAt).getTime()) / (1000 * 60 * 60 * 24))
                            return days === 0 ? t('dashboard.aiAdvice.updatedToday') : t('dashboard.aiAdvice.updatedAgo', { days })
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRefreshSuggestion() }}
                    disabled={aiLoading}
                    className="p-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-amber-600 dark:text-amber-400 ${aiLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                
                {/* AGC: Advice content */}
                <div className="flex-1 flex items-center">
                  <p className={`text-xs text-gray-700 dark:text-gray-300 leading-relaxed ${expandedAIAdvice ? '' : 'line-clamp-3'}`}>
                    {aiLoading ? t('dashboard.aiAdvice.refreshing') : (aiSuggestion?.mainTip || t('dashboard.aiAdvice.harvestApproaching'))}
                  </p>
                </div>
                
                {/* AGC: Expanded AI Advice - Show more details */}
                {expandedAIAdvice && (
                  <div className="mt-3 pt-3 border-t border-amber-200/30 dark:border-amber-700/30 space-y-2 animate-fade-in">
                    {(aiSuggestion?.tips || [t('dashboard.aiAdvice.tip1'), t('dashboard.aiAdvice.tip2'), t('dashboard.aiAdvice.tip3')]).map((tip, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">{tip}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* AGC: Dot indicators - 3 slides */}
        <div className="flex justify-center gap-1.5 mt-2">
          {[0, 1, 2].map((index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-droid-600 dark:bg-droid-400 w-4' 
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* AGC: Quick Actions */}
      {/* AGC: Desktop col 2 — atalhos below carousel, no row constraint */}
      {/* AGC: Mobile col 1 — original 4-column shortcuts grid unchanged */}
      <div className={isDesktop ? '' : 'px-4 mt-4'}>
        <h3 className="text-gray-900 dark:text-white mb-3 font-semibold text-sm">
          {t('dashboard.shortcuts')}
        </h3>
        {isDesktop ? (
          // AGC: Desktop: 5 specific shortcuts in a single row (no Ver Mapa)
          <div className="grid grid-cols-5 gap-2">
            <DesktopShortcutButton
              icon={<MapPin className="w-4 h-4" />}
              label={t('dashboard.shortcutNewLot')}
              onPress={() => onNavigate('novo-lote')}
            />
            <DesktopShortcutButton
              icon={<ListTodo className="w-4 h-4" />}
              label={t('dashboard.shortcutNewTask')}
              onPress={() => onNavigate('new-task')}
            />
            <DesktopShortcutButton
              icon={<Tractor className="w-4 h-4" />}
              label={t('dashboard.shortcutNewMachinery')}
              onPress={() => onNavigate('machine-form')}
            />
            <DesktopShortcutButton
              icon={<DollarSign className="w-4 h-4" />}
              label={t('dashboard.shortcutNewFinance')}
              onPress={() => onNavigate('new-revenue')}
            />
            <DesktopShortcutButton
              icon={<Package className="w-4 h-4" />}
              label={t('dashboard.shortcutNewInventory')}
              onPress={() => onNavigate('inventory')}
            />
          </div>
        ) : (
          // AGC: Mobile: original 4-column grid unchanged
          <div className="grid grid-cols-4 gap-2 stagger-children">
            {filteredQuickActions.map((action) => (
              <QuickActionButton
                key={action.id}
                action={action}
                onPress={() => onNavigate(action.screen as Screen)}
              />
            ))}
          </div>
        )}
      </div>

      {/* AGC: End of right col row 1 wrapper (Clima + Atalhos) */}
      </div>

      {/* AGC: Desktop col 1 row 2 — Lotes below revenue card, aligned with Atividades on the right */}
      <div className={isDesktop ? '' : 'mt-6'} style={isDesktop ? { gridColumn: '1', gridRow: '2' } : undefined}>
        <DashboardLotsSection
          onLotClick={(lotId) => {
            // AGC: Navigate to lot-page (correct, canonical lot detail page)
            onNavigate('lot-page', { plotId: lotId })
          }}
          onViewAllClick={() => onNavigate('lotes-list')}
          compact={isDesktop}
        />
      </div>

      {/* AGC: Recent Activities */}
      {/* AGC: Desktop col 2 row 2 — aligned with Lotes on the left (same grid row) */}
      {/* AGC: Mobile: original position, flows after Lotes */}
      <div
        className={isDesktop ? '' : 'px-4 mt-2 pb-6'}
        style={isDesktop ? { gridColumn: '2', gridRow: '2' } : undefined}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-gray-900 dark:text-white font-semibold text-sm">
            {t('dashboard.recentActivities')}
          </h3>
          <button
            onClick={() => onNavigate('notifications')}
            className="flex items-center gap-1 text-sm text-navy-600 dark:text-navy-400 font-medium hover:text-navy-700 dark:hover:text-navy-300 transition-colors"
          >
            {t('dashboard.viewAll')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <Card variant="default" padding="none">
          {activities.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <p>{t('dashboard.noRecentActivities')}</p>
            </div>
          ) : (
            // AGC: Desktop: fixed window of 3 entries — overflow scrolls inside the card
            // AGC: Mobile: show up to 5 entries, no fixed height
            <div
              className="divide-y divide-gray-100 dark:divide-slate-700 stagger-children overflow-y-auto"
              style={isDesktop ? { maxHeight: '192px' } : undefined}
            >
              {activities.slice(0, isDesktop ? 3 : 5).map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </Card>
      </div>

    </div>
  )
}

/**
 * AGC: Quick Action Button with Classic Navy Theme
 */
interface QuickActionButtonProps {
  action: QuickAction
  onPress: () => void
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ action, onPress }) => {
  const { t } = useTranslation()
  const IconComponent = iconMap[action.iconName] || MapPin

  // AGC: All shortcuts use green - consistent with crop icons on lots
  const colorClass = 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'

  return (
    <button
      onClick={onPress}
      className="
        flex flex-col items-center
        p-3 rounded-xl
        bg-white dark:bg-slate-800
        border border-gray-100 dark:border-slate-700
        shadow-card
        transition-all duration-200
        hover:shadow-card-hover hover:border-gray-200 dark:hover:border-slate-600
        active:scale-95
      "
    >
      {/* AGC: Icon with rounded bg - always at top, vertically aligned */}
      <div className={`
        w-10 h-10 rounded-lg
        flex items-center justify-center
        flex-shrink-0
        mb-2
        ${colorClass}
      `}>
        <IconComponent className="w-5 h-5" />
      </div>
      {/* AGC: Text container with fixed height to keep icons aligned */}
      <div className="h-8 flex items-center">
        <span className="text-xs text-gray-700 dark:text-gray-300 text-center leading-tight font-medium">
          {t(action.label)}
        </span>
      </div>
    </button>
  )
}

/**
 * AGC: DesktopShortcutButton - compact 3×2 grid button used only on desktop Atalhos panel
 */
interface DesktopShortcutButtonProps {
  icon: React.ReactNode
  label: string
  onPress: () => void
}

const DesktopShortcutButton: React.FC<DesktopShortcutButtonProps> = ({ icon, label, onPress }) => (
  <button
    onClick={onPress}
    className="
      flex flex-col items-center justify-center gap-1.5
      p-2 rounded-xl
      bg-white dark:bg-slate-800
      border border-gray-100 dark:border-slate-700
      shadow-card
      transition-all duration-200
      hover:shadow-card-hover hover:border-emerald-200 dark:hover:border-emerald-700
      active:scale-95
      min-h-[64px]
    "
  >
    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 flex-shrink-0">
      {icon}
    </div>
    <span className="text-[10px] text-gray-700 dark:text-gray-300 text-center leading-tight font-medium w-full">
      {label}
    </span>
  </button>
)

/**
 * AGC: Activity Item with Classic Navy Theme
 */
interface ActivityItemProps {
  activity: Activity
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  const IconComponent = iconMap[activity.iconName] || Droplets

  // AGC: Icon color based on activity type
  const iconColorClasses: Record<string, string> = {
    task: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    weather: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    crop: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    financial: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    system: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400',
  }

  return (
    <div className="flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
      <div className={`
        flex-shrink-0 w-9 h-9 rounded-lg
        flex items-center justify-center
        ${iconColorClasses[activity.type] || iconColorClasses.system}
      `}>
        <IconComponent className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
          {activity.title}
        </p>
        {activity.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {activity.description}
          </p>
        )}
      </div>
      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
        {formatRelativeTime(activity.timestamp)}
      </span>
    </div>
  )
}

export default Home
